import * as THREE from 'three';
import { cloudFieldGLSL, cloudUniforms } from './clouds';
import { WIND_DIR, WIND_GUST } from './wind';

// RAIN: real 3D rain, cheap. A pool of instanced streak particles in WORLD space in a column around the
// player. Each falls from spawn height to the ground (GPU, no CPU per-particle work) and is drawn as a thin,
// SHORT billboard streak oriented along its fall velocity. It uses the standard transform + the renderer's
// log-depth, so terrain OCCLUDES it -> you only see drops in the air landing all around you, at any camera
// angle/zoom. LOCALISED by the live cloud field (oxCloud, shared with the cloud sheet): a streak is hidden
// where there's no cloud overhead, so rain falls in patches that drift with the clouds, only while a storm is
// active (uStorm). The column RADIUS ADAPTS to the camera zoom so rain always fills the view (no visible
// "cube"), and the streaks fade out toward the column rim (no hard ring). Streaks SCREEN-blend (premultiplied,
// lighten-only) so they read as light against BOTH a bright sky and a dark storm -- never a black smear.
// Cosmetic, client-only.

const COUNT = 14000;    // POOL size (buffer max). The DRAWN count scales with the column area each frame (see
                        // update) to hold density ~constant -- too many streaks in a small column pile up and
                        // read as a dark/busy stipple, so we draw only ~DENSITY per m^2.
const DENSITY = 0.26;   // drawn streaks per m^2 of the column footprint (tuned: present but distinct, not a dark stipple)
const R_MIN = 46;       // close-in / first-person column radius
const R_MAX = 150;      // covers a fully zoomed-out iso frame
const SPAWN_H = 30;     // column height (spawn height above the ground floor)
const FALL = 16.5;      // BASE fall speed (m/s); each drop multiplies this by 3..5x (per-drop random)
const HALF_W = 0.018;   // streak half-width (world m, thin)
const HALF_L = 0.16;    // streak half-length (world m, tiny rain)

export class RainLayer {
  readonly mesh: THREE.Mesh;
  private readonly u = {
    uTime: { value: 0 },
    uCloudTime: cloudUniforms.uTime,       // shared with the cloud sheet -> rain falls under the clouds you see
    uCloudCover: cloudUniforms.uCloudCover,
    uStorm: { value: 0 },                  // storm intensity 0..1 (ramp/peak/fade)
    uSnow: { value: 0 },                   // 0 rain .. 1 SNOW FLURRIES (slow drifting flakes, not wet streaks)
    uWind: { value: new THREE.Vector2(5.0, 2.0) }, // horizontal wind (m/s) -> streak slant
    uR: { value: R_MIN }, uSpawnH: { value: SPAWN_H }, uFall: { value: FALL }, uHalfW: { value: HALF_W }, uHalfL: { value: HALF_L },
  };

  constructor() {
    const geo = new THREE.InstancedBufferGeometry();
    geo.instanceCount = COUNT;
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const seed = new Float32Array(COUNT * 4);   // (relX -1..1, relZ -1..1, phase 0..1, keep 0..1)
    const meta = new Float32Array(COUNT * 4);   // (widthMul, lengthMul, alpha, fallSpeedMul 3..5)
    for (let i = 0; i < COUNT; i++) {
      const a = Math.random() * Math.PI * 2, rad = Math.sqrt(Math.random());
      seed[i * 4] = Math.cos(a) * rad; seed[i * 4 + 1] = Math.sin(a) * rad;
      seed[i * 4 + 2] = Math.random(); seed[i * 4 + 3] = Math.random();
      meta[i * 4] = 0.8 + Math.random() * 0.7;          // width mul
      meta[i * 4 + 1] = 0.85 + Math.random() * 0.5;     // length mul (tiny streaks ~0.27..0.43 m)
      meta[i * 4 + 2] = 0.08 + Math.random() * 0.16;    // alpha 8..24%
      meta[i * 4 + 3] = 3.0 + Math.random() * 2.0;      // per-drop fall-speed factor 3..5x
    }
    geo.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seed, 4));
    geo.setAttribute('iMeta', new THREE.InstancedBufferAttribute(meta, 4));

    const mat = new THREE.ShaderMaterial({
      uniforms: this.u,
      transparent: true,
      depthWrite: false,   // don't write depth (streaks layer freely, never fight each other) ...
      depthTest: true,     // ... but DO test, so terrain occludes them -> real 3D
      blending: THREE.CustomBlending,        // ADDITIVE: a streak only ADDS light -> can NEVER darken, even over
      blendEquation: THREE.AddEquation,      // an HDR-bright sky (screen blend went dark there -> black specks).
      blendSrc: THREE.OneFactor,             // src is premultiplied (RGB = streak * a) in the fragment
      blendDst: THREE.OneFactor,             // result = streak*a + scene -> always lighter
      defines: { USE_LOGDEPTHBUF: '' }, // the scene uses a logarithmic depth buffer; match it so terrain occludes the streaks
      vertexShader: `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute vec4 iSeed;
        attribute vec4 iMeta;
        uniform float uTime, uStorm, uR, uSpawnH, uFall, uHalfW, uHalfL, uCloudCover, uCloudTime, uSnow;
        uniform vec2 uWind;
        varying float vA; varying vec2 vC; varying float vFar;
        ${cloudFieldGLSL}
        void main() {
          vec3 base = vec3(modelMatrix[3][0], modelMatrix[3][1], modelMatrix[3][2]); // player XZ + ground Y
          float fall = uFall * iMeta.w * mix(1.0, 0.16, uSnow);  // per-drop fall speed; SNOW floats down slowly
          float p = fract(iSeed.z + uTime * (fall / uSpawnH));   // fall progress 0 top .. 1 ground
          vec2 wxz = base.xz + iSeed.xy * uR + uWind * (p * (uSpawnH / fall)); // drift with wind as it falls
          wxz += uSnow * vec2(sin(uTime * 1.35 + iSeed.z * 43.0), cos(uTime * 1.1 + iSeed.w * 37.0)) * 0.9 * p; // flakes WANDER
          float py = base.y + uSpawnH * (1.0 - p);
          vec3 center = vec3(wxz.x, py, wxz.y);

          // RAIN MASK: only where the cloud field is dense (matches the visible clouds) AND a storm is active.
          float cloud = oxCloud(wxz, uCloudTime) + uCloudCover;
          float mask = smoothstep(0.42, 0.60, cloud) * uStorm; // rain only under dense cloud, and only in a storm
          if (iSeed.w > mask) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; } // dry here -> hide

          // velocity-aligned billboard streak: long axis = the world fall direction; width billboarded to camera.
          vec3 vel = vec3(uWind.x, -fall, uWind.y);
          vec3 longA = normalize(vel);
          vec3 toCam = normalize(cameraPosition - center);
          vec3 sc = cross(longA, toCam);
          float scl = length(sc);
          vec3 shortA = scl > 1e-3 ? sc / scl : normalize(cross(longA, vec3(0.0, 0.0, 1.0))); // guard edge-on degenerate

          // PERSPECTIVE VEIL: far drops would shrink to sharp sub-pixel dots and read as static "noise". Grow
          // them with distance (wider especially) so they stay soft, overlap, and blur into a rain CURTAIN, and
          // fade them a touch so it's a subtle veil rather than hard specks. Near drops stay tiny + crisp.
          float camDist = length(cameraPosition - center);
          float farF = smoothstep(28.0, 120.0, camDist);         // 0 near .. 1 far
          float lenF = uHalfL * iMeta.y * (1.0 + farF * 2.2) * mix(1.0, 0.18, uSnow); // snow: short = a round flake, not a streak
          float widF = uHalfW * iMeta.x * (1.0 + farF * 5.0) * mix(1.0, 2.6, uSnow);  // ... and plumper
          vec3 corner = center + longA * position.y * lenF + shortA * position.x * widF;
          gl_Position = projectionMatrix * viewMatrix * vec4(corner, 1.0);
          #include <logdepthbuf_vertex>

          float rr = length(iSeed.xy);                           // 0 centre .. 1 disk rim
          float edge = 1.0 - smoothstep(0.72, 1.0, rr);          // dissolve toward the rim -> no hard ring
          float fade = smoothstep(0.0, 0.06, p) * (1.0 - smoothstep(0.9, 1.0, p)); // fade in at top, out as it lands
          float endOn = smoothstep(0.18, 0.55, scl);             // looking UP/DOWN the fall foreshortens streaks to dots -> fade those out (kills the "noise" overhead)
          vA = iMeta.z * fade * edge * (1.0 - 0.45 * farF) * endOn; vC = position.xy; vFar = farF;
        }`,
      fragmentShader: `
        #include <common>
        #include <logdepthbuf_pars_fragment>
        uniform float uSnow;
        varying float vA; varying vec2 vC; varying float vFar;
        void main() {
          #include <logdepthbuf_fragment>
          // max(0.0, ...) is CRITICAL: 1 - abs(vC.x) can go slightly NEGATIVE from edge interpolation, and
          // pow(negative, fractional) = NaN -> a black fragment that the discard below misses (NaN < x is false).
          // That NaN was the "black specks" (poisons any blend, so it read dark on bright sky / invisible on dark).
          float across = pow(max(0.0, 1.0 - abs(vC.x)), mix(1.3, 0.5, vFar)); // softer/broader across at distance -> blurred veil
          float ends = 1.0 - smoothstep(mix(0.7, 0.35, vFar), 1.0, abs(vC.y)); // softer tips at distance
          float shape = across * ends;
          if (!(shape >= 0.06)) discard;                        // NaN-safe (discards any stray NaN too); transparent edges don't render
          float a = shape * vA * mix(1.0, 1.7, uSnow);          // coverage * alpha; flakes read brighter
          vec3 streak = mix(vec3(0.95, 0.97, 1.0), vec3(1.0), uSnow); // near-white drop -> pure white flake
          gl_FragColor = vec4(streak * a, a);                   // premultiplied (additive: result = streak*a + scene)
        }`,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;     // the box always rides the player; per-streak clip culls the rest
    this.mesh.renderOrder = 4;
    this.mesh.visible = false;
  }

  setStorm(intensity: number): void { this.u.uStorm.value = intensity; this.mesh.visible = intensity > 0.005; }

  // 0 = rain, 1 = snow flurries (snow biome / above the snow line). Eased by the caller per frame.
  setSnowiness(v: number): void { this.u.uSnow.value += (v - this.u.uSnow.value) * 0.06; }

  // Ride the column around the player at ground level; advance the fall clock; ADAPT the radius to the camera
  // zoom so rain always fills the view (no visible cube edge), eased so it doesn't pop when zooming.
  update(focusX: number, groundY: number, focusZ: number, time: number, zoom: number, firstPerson: boolean): void {
    this.mesh.position.set(focusX, groundY, focusZ);
    this.u.uTime.value = time;
    // Slant the streaks along the GLOBAL wind (same direction the trees + grass lean), harder in a gust: a calm
    // shower falls near-vertical, a gust drives it sideways. 3..~18 m/s horizontal drift.
    const ws = 3 + WIND_GUST.value * 12;
    this.u.uWind.value.set(WIND_DIR.value.x * ws, WIND_DIR.value.y * ws);
    const halfH = 14 / Math.max(zoom, 0.12);                    // framed half-height at focus (FRUSTUM=14, minZoom=0.12)
    const want = firstPerson ? R_MIN : Math.min(R_MAX, Math.max(R_MIN, halfH * 1.5));
    this.u.uR.value += (want - this.u.uR.value) * 0.15;         // ease toward the wanted radius
    // Draw only enough streaks to hold DENSITY/m^2 over the current column -> distinct streaks at any zoom,
    // never the dense pile-up that stipples into dark noise.
    const r = this.u.uR.value;
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = Math.min(COUNT, Math.max(500, Math.round(DENSITY * Math.PI * r * r)));
  }
}
